import type { Icon } from "@phosphor-icons/react";
import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { ChartLineUpIcon } from "@phosphor-icons/react/dist/csr/ChartLineUp";
import { MegaphoneIcon } from "@phosphor-icons/react/dist/csr/Megaphone";
import { ReceiptIcon } from "@phosphor-icons/react/dist/csr/Receipt";
import { WalletIcon } from "@phosphor-icons/react/dist/csr/Wallet";
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from "@volca/ui/components/ui/command";
import { NAV_ROUTES } from "@/app-shell/nav-items";
import type { NavItem } from "@/app-shell/use-nav-items";
import type { SettingsNav } from "@/app-shell/use-settings-nav";
import { CHANGELOG_PATH } from "@/changelog/routes";
import { FindReceiptItem } from "./ap-pages";
import { HelpGroup } from "./help-group";
import { ImpersonationCommands } from "./impersonation-commands";
import type { CommandPage } from "./pages";
import { SupportGroup } from "./support-group";

interface CommandDestination {
  key: string;
  label: string;
  value: string;
  to: string;
  icon: Icon;
  afterNavigate?: () => void;
}

const CHANGELOG_DESTINATION: CommandDestination = {
  key: CHANGELOG_PATH,
  label: "What’s new",
  value:
    "What’s new what's new changelog release notes product updates /changelog",
  to: CHANGELOG_PATH,
  icon: MegaphoneIcon,
};

const pageDestinations = (items: NavItem[]): CommandDestination[] =>
  items.flatMap((item) => [
    {
      key: item.href,
      label: item.label,
      value: `${item.label} ${item.to}`,
      to: item.to,
      icon: item.icon,
    },
    // Rows nested under another (an agent's pages) are named with it.
    ...item.sections
      .flatMap((section) => [
        section,
        ...(section.children ?? []).map((child) => ({
          ...child,
          label: `${section.label} › ${child.label}`,
        })),
      ])
      .filter((section) => section.to !== item.to && !section.pending)
      .map((section) => ({
        key: `${item.href}:${section.to}`,
        label: section.label,
        value: `${item.label} ${section.label} ${section.to}`,
        to: section.to,
        icon: section.icon ?? item.icon,
      })),
  ]);

const settingsDestinations = (
  settings: SettingsNav | null,
): CommandDestination[] =>
  (settings?.items ?? []).map((item) => ({
    key: item.href,
    label: item.title,
    value: `Settings ${item.title} ${item.href}`,
    to: item.href,
    icon: item.icon,
    afterNavigate: settings?.onNavigate,
  }));

const CommandDestinationGroup = ({
  heading,
  destinations,
  onSelect,
}: {
  heading: string;
  destinations: CommandDestination[];
  onSelect: (destination: CommandDestination) => void;
}) => {
  if (destinations.length === 0) return null;

  return (
    <CommandGroup heading={heading}>
      {destinations.map((destination) => {
        const DestinationIcon = destination.icon;
        return (
          <CommandItem
            key={destination.key}
            value={destination.value}
            onSelect={() => onSelect(destination)}
          >
            <DestinationIcon />
            <span>{destination.label}</span>
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
};

const DrillItem = ({
  label,
  value,
  icon: ItemIcon,
  onSelect,
}: {
  label: string;
  value: string;
  icon: Icon;
  onSelect: () => void;
}) => (
  <CommandItem value={value} onSelect={onSelect}>
    <ItemIcon />
    <span>{label}</span>
    <CommandShortcut>
      <CaretRightIcon />
    </CommandShortcut>
  </CommandItem>
);

export const hasCommandHref = (items: NavItem[], href: string) =>
  items.some((item) => item.href === href);

export const CommandMenuRoot = ({
  items,
  settings,
  open,
  query,
  showHelp,
  themeLabel,
  ThemeIcon,
  onPush,
  onGo,
  onTheme,
  onImpersonate,
  onClose,
}: {
  items: NavItem[];
  settings: SettingsNav | null;
  open: boolean;
  query: string;
  showHelp: boolean;
  themeLabel: string;
  ThemeIcon: Icon;
  onPush: (page: CommandPage) => void;
  onGo: (to: string, after?: () => void) => void;
  onTheme: () => void;
  onImpersonate?: () => void;
  onClose: () => void;
}) => {
  const showPayroll = hasCommandHref(items, NAV_ROUTES.payBreakdown);
  const showProfitability = hasCommandHref(items, NAV_ROUTES.profitability);
  const showAp = hasCommandHref(items, NAV_ROUTES.accountsPayable);

  return (
    <>
      <CommandEmpty>No results.</CommandEmpty>
      <CommandDestinationGroup
        heading="Go to"
        destinations={[...pageDestinations(items), CHANGELOG_DESTINATION]}
        onSelect={(destination) =>
          onGo(destination.to, destination.afterNavigate)
        }
      />
      {(showPayroll || showProfitability || showAp) && (
        <CommandGroup heading="Open">
          {showPayroll && (
            <DrillItem
              label="Pay periods"
              value="payroll pay periods"
              icon={WalletIcon}
              onSelect={() => onPush({ kind: "payroll-periods" })}
            />
          )}
          {showProfitability && (
            <DrillItem
              label="Reports"
              value="profitability reports new report"
              icon={ChartLineUpIcon}
              onSelect={() => onPush({ kind: "profitability" })}
            />
          )}
          {showAp && (
            <DrillItem
              label="Find invoice"
              value="find invoice accounts payable"
              icon={ReceiptIcon}
              onSelect={() => onPush({ kind: "ap-invoices" })}
            />
          )}
          {showAp && open && <FindReceiptItem onOpen={onPush} />}
          {showAp && (
            <DrillItem
              label="Review next invoice"
              value="review next invoice review queue"
              icon={ReceiptIcon}
              onSelect={() => onPush({ kind: "ap-review" })}
            />
          )}
        </CommandGroup>
      )}
      <CommandDestinationGroup
        heading="Settings"
        destinations={settingsDestinations(settings)}
        onSelect={(destination) =>
          onGo(destination.to, destination.afterNavigate)
        }
      />
      {showHelp && (
        <HelpGroup
          query={query}
          onBrowse={() => onPush({ kind: "help" })}
          onGo={onGo}
        />
      )}
      {onImpersonate && (
        <ImpersonationCommands
          onImpersonate={onImpersonate}
          onClose={onClose}
        />
      )}
      <SupportGroup onClose={onClose} />
      <CommandGroup heading="Appearance">
        <CommandItem
          value="use dark mode use light mode appearance theme"
          onSelect={onTheme}
        >
          <ThemeIcon />
          <span>{themeLabel}</span>
        </CommandItem>
      </CommandGroup>
    </>
  );
};
